import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { SignalMesh } from "./SignalMesh";
import { PdfAccent } from "./PdfAccent";
import { PdfChip } from "./PdfChip";
import { PdfCover } from "./PdfCover";
import { PdfPage } from "./PdfPage";
import { buildPdfContentUnits } from "./pdfBuildUnits";
import { PDF_EXPORT_ROOT_ID } from "./pdfDimensions";
import { packPdfUnits } from "./pdfPagination";
import type { ReportPdfModel } from "./reportTypes";

type PdfPaginatorProps = {
  model: ReportPdfModel;
};

export function PdfPaginator({ model }: PdfPaginatorProps) {
  const units = useMemo(() => buildPdfContentUnits(model), [model]);
  const [pageGroups, setPageGroups] = useState<number[][] | null>(null);
  const measureRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const measureRoot = measureRef.current;
    if (!measureRoot) {
      return;
    }

    const exportRoot = document.getElementById(PDF_EXPORT_ROOT_ID);
    exportRoot?.removeAttribute("data-pdf-layout-ready");

    const unitEls = Array.from(measureRoot.querySelectorAll<HTMLElement>("[data-pdf-unit]"));
    if (unitEls.length !== units.length) {
      return;
    }

    const heights = unitEls.map((el) => {
      const section = el.querySelector<HTMLElement>(".pdf-section-block");
      const target = section ?? el;
      return Math.ceil(target.getBoundingClientRect().height);
    });
    const groups = packPdfUnits(heights);
    setPageGroups(groups);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        exportRoot?.setAttribute("data-pdf-layout-ready", "true");
      });
    });
  }, [units]);

  const contentPageCount = pageGroups?.length ?? 0;
  const totalPages = 1 + contentPageCount + 1;
  const closingPageNumber = totalPages;

  return (
    <>
      <div ref={measureRef} className="pdf-measure-root" aria-hidden>
        {units.map((unit) => (
          <div key={unit.id} data-pdf-unit={unit.id} className="pdf-measure-unit">
            <div className="pdf-flow-section">{unit.node}</div>
          </div>
        ))}
      </div>

      <PdfPage pageNumber={1}>
        <PdfCover companyName={model.companyName} />
      </PdfPage>

      {pageGroups?.map((indices, pageIdx) => {
        const pageNumber = pageIdx + 2;
        const soleUnit = indices.length === 1 ? units[indices[0]] : undefined;
        const pageClasses = [
          soleUnit?.pageClassName,
          indices.length === 1 && soleUnit?.fillPage ? "pdf-page--sole" : "",
        ]
          .filter(Boolean)
          .join(" ");

        return (
          <PdfPage
            key={indices.join("-")}
            pageNumber={pageNumber}
            className={pageClasses}
          >
            <div className="pdf-page-flow">
              {indices.map((unitIndex) => (
                <div key={units[unitIndex].id} className="pdf-flow-section">
                  {units[unitIndex].node}
                </div>
              ))}
            </div>
          </PdfPage>
        );
      })}

      {pageGroups ? (
        <PdfPage pageNumber={closingPageNumber} dark centered>
          <SignalMesh className="pdf-closing-mesh h-full w-full" />
          <div className="pdf-closing-content">
            <PdfChip dark size="sm" className="mb-6">
              Closing
            </PdfChip>
            <h2 className="font-display font-semibold tracking-tight text-white">
              Focus on measurable
              <br />
              <PdfAccent>business outcomes.</PdfAccent>
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-white/70">
              This assessment identifies where AI can improve operational and financial performance before major
              investment.
            </p>
            <p className="mt-8 text-[13px] text-white/55">connect@gowitek.com</p>
          </div>
        </PdfPage>
      ) : null}
    </>
  );
}
